/**
 * Modal de gerenciamento de pages do grupo da Esteira.
 *
 * Permite:
 *   - Listar as pages atualmente vinculadas (group.pages_meta enriquecido)
 *   - Remover individual (com cancelamento dos pendings dela)
 *   - Sincronizar por categoria — adiciona todas pages ATIVAS das
 *     categorias selecionadas que o user tenha permissão
 *
 * Backend:
 *   DELETE /esteira/groups/{id}/pages/{page_id}
 *   POST   /esteira/groups/{id}/sync-from-categories  { category_ids: [] }
 */

import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2, Loader2, FolderSync, RefreshCw, Globe, AlertTriangle } from 'lucide-react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import {
    removePageFromEsteiraGroup,
    syncEsteiraGroupFromCategories,
    batchRemovePagesFromEsteiraGroup,
    type EsteiraSyncFromCategoriesResult,
    type EsteiraBatchRemovePagesResult,
} from '@/services/api';
import type { EsteiraGroup, EsteiraFormOptionsResponse } from '@/types/esteira';

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    group: EsteiraGroup;
    formOptions?: EsteiraFormOptionsResponse;
}

export default function ManageEsteiraPagesDialog({ open, onOpenChange, group, formOptions }: Props) {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const [selectedCategoryIds, setSelectedCategoryIds] = useState<Set<number>>(new Set());
    const [selectedPageIds, setSelectedPageIds] = useState<Set<number>>(new Set());

    const invalidate = () => {
        queryClient.invalidateQueries({ queryKey: ['esteira-group', group.id] });
        queryClient.invalidateQueries({ queryKey: ['esteira-history', group.id] });
        queryClient.invalidateQueries({ queryKey: ['esteira-upcoming', group.id] });
    };

    // Map id → label preferindo pages_meta (do backend, inclui inativas) +
    // fallback no formOptions (só ativas).
    const formPagesById = useMemo(() => {
        const m = new Map<number, { name: string | null; account: string | null }>();
        (formOptions?.pages ?? []).forEach(p => m.set(p.id, {
            name: p.page_name, account: p.meta_user_name,
        }));
        return m;
    }, [formOptions]);

    // Pre-calcula quantas pages NOVAS cada categoria adicionaria (preview)
    const pagesByCategory = useMemo(() => {
        const m = new Map<number, number[]>();
        (formOptions?.pages ?? []).forEach(p => {
            if (p.category_id == null) return;
            const arr = m.get(p.category_id) || [];
            arr.push(p.id);
            m.set(p.category_id, arr);
        });
        return m;
    }, [formOptions]);

    const currentPageIds = new Set(group.page_ids);
    const previewAdded = useMemo(() => {
        let count = 0;
        const seen = new Set<number>();
        selectedCategoryIds.forEach(catId => {
            (pagesByCategory.get(catId) || []).forEach(pid => {
                if (!seen.has(pid) && !currentPageIds.has(pid)) {
                    seen.add(pid);
                    count++;
                }
            });
        });
        return count;
    }, [selectedCategoryIds, pagesByCategory, currentPageIds]);

    // Lookup rápido de page meta (vem do backend, inclui inativas, exclui deletadas).
    const pagesMeta = group.pages_meta || [];
    const metaById = useMemo(
        () => new Map(pagesMeta.map(m => [m.id, m])),
        [pagesMeta],
    );

    const removeMut = useMutation({
        mutationFn: (pageId: number) => removePageFromEsteiraGroup(group.id, pageId),
        onSuccess: () => {
            toast({ title: 'Página removida', description: 'Pendings desta página foram cancelados.' });
            invalidate();
        },
        onError: (err: any) => toast({
            title: 'Erro ao remover',
            description: err?.response?.data?.detail || String(err),
            variant: 'destructive',
        }),
    });

    const batchRemoveMut = useMutation({
        mutationFn: (pageIds: number[]) => batchRemovePagesFromEsteiraGroup(group.id, pageIds),
        onSuccess: (data: EsteiraBatchRemovePagesResult) => {
            const partes = [
                `${data.removed} removida${data.removed === 1 ? '' : 's'}`,
                data.skipped_no_permission > 0 ? `${data.skipped_no_permission} sem permissão` : null,
                data.skipped_not_in_group > 0 ? `${data.skipped_not_in_group} não estavam no grupo` : null,
            ].filter(Boolean).join(' · ');
            toast({ title: 'Remoção em lote concluída', description: partes });
            setSelectedPageIds(new Set());
            invalidate();
        },
        onError: (err: any) => toast({
            title: 'Erro ao remover em lote',
            description: err?.response?.data?.detail || String(err),
            variant: 'destructive',
        }),
    });

    const togglePageSelection = (id: number) => {
        const next = new Set(selectedPageIds);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setSelectedPageIds(next);
    };
    const selectAllPages = () => setSelectedPageIds(new Set(group.page_ids));
    const selectDeletedPages = () => {
        const deletedIds = group.page_ids.filter(pid => !metaById.has(pid) && !formPagesById.has(pid));
        setSelectedPageIds(new Set(deletedIds));
    };
    const selectInactivePages = () => {
        const inactiveIds = group.page_ids.filter(pid => {
            const m = metaById.get(pid);
            // Inclui as deletadas (sem meta) + as com is_usable false
            return !m || m.is_usable === false;
        });
        setSelectedPageIds(new Set(inactiveIds));
    };
    const clearSelection = () => setSelectedPageIds(new Set());

    const syncMut = useMutation({
        mutationFn: (categoryIds: number[]) => syncEsteiraGroupFromCategories(group.id, categoryIds),
        onSuccess: (data: EsteiraSyncFromCategoriesResult) => {
            const partes = [
                `${data.added} adicionada${data.added === 1 ? '' : 's'}`,
                data.skipped_existing > 0 ? `${data.skipped_existing} já vinculada${data.skipped_existing === 1 ? '' : 's'}` : null,
                data.skipped_no_permission > 0 ? `${data.skipped_no_permission} sem permissão` : null,
            ].filter(Boolean).join(' · ');
            toast({ title: 'Sincronização concluída', description: partes });
            setSelectedCategoryIds(new Set());
            invalidate();
        },
        onError: (err: any) => toast({
            title: 'Erro ao sincronizar',
            description: err?.response?.data?.detail || String(err),
            variant: 'destructive',
        }),
    });

    const toggleCategory = (id: number) => {
        const next = new Set(selectedCategoryIds);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setSelectedCategoryIds(next);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[680px] max-h-[85vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle>Gerenciar páginas — {group.name}</DialogTitle>
                    <DialogDescription>
                        {group.page_ids.length} página{group.page_ids.length === 1 ? '' : 's'} vinculada{group.page_ids.length === 1 ? '' : 's'} no grupo.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto space-y-6 pr-1">
                    {/* SEÇÃO 1: Sync por categoria */}
                    <section className="space-y-3 border rounded-lg p-4 bg-violet-50/40 border-violet-200/60">
                        <div className="flex items-center gap-2">
                            <FolderSync className="h-4 w-4 text-violet-700" />
                            <h3 className="text-sm font-semibold text-violet-900">
                                Sincronizar por categoria
                            </h3>
                        </div>
                        <p className="text-xs text-violet-700/80">
                            Adiciona todas as páginas ATIVAS das categorias selecionadas que você tem acesso. Páginas já vinculadas são ignoradas.
                        </p>

                        {(formOptions?.categories?.length ?? 0) === 0 ? (
                            <p className="text-xs text-gray-500 italic">Nenhuma categoria disponível.</p>
                        ) : (
                            <div className="grid grid-cols-2 gap-2 max-h-44 overflow-y-auto bg-white rounded border p-2">
                                {formOptions!.categories.map(cat => {
                                    const total = (pagesByCategory.get(cat.id) || []).length;
                                    return (
                                        <label
                                            key={cat.id}
                                            className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 px-2 py-1 rounded"
                                        >
                                            <Checkbox
                                                checked={selectedCategoryIds.has(cat.id)}
                                                onCheckedChange={() => toggleCategory(cat.id)}
                                            />
                                            <span className="truncate flex-1">{cat.name}</span>
                                            <span className="text-[10px] text-gray-400">{total}</span>
                                        </label>
                                    );
                                })}
                            </div>
                        )}

                        <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-600">
                                {selectedCategoryIds.size === 0
                                    ? 'Selecione 1+ categorias'
                                    : `${previewAdded} página${previewAdded === 1 ? '' : 's'} nova${previewAdded === 1 ? '' : 's'} será${previewAdded === 1 ? '' : 'ão'} adicionada${previewAdded === 1 ? '' : 's'}`}
                            </span>
                            <Button
                                size="sm"
                                disabled={selectedCategoryIds.size === 0 || syncMut.isPending}
                                onClick={() => syncMut.mutate(Array.from(selectedCategoryIds))}
                            >
                                {syncMut.isPending
                                    ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" />Sincronizando...</>
                                    : <><RefreshCw className="h-3 w-3 mr-1" />Sincronizar</>}
                            </Button>
                        </div>
                    </section>

                    {/* SEÇÃO 2: Lista atual + seleção batch */}
                    <section className="space-y-2">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                            <h3 className="text-sm font-semibold text-gray-900">
                                Páginas vinculadas ({group.page_ids.length})
                            </h3>
                            {group.page_ids.length > 0 && (
                                <div className="flex items-center gap-1 text-[11px]">
                                    <button
                                        type="button"
                                        className="text-violet-600 hover:underline px-1.5"
                                        onClick={selectAllPages}
                                    >
                                        Todas
                                    </button>
                                    <span className="text-gray-300">·</span>
                                    <button
                                        type="button"
                                        className="text-amber-700 hover:underline px-1.5"
                                        onClick={selectInactivePages}
                                    >
                                        Só inativas/excluídas
                                    </button>
                                    <span className="text-gray-300">·</span>
                                    <button
                                        type="button"
                                        className="text-red-700 hover:underline px-1.5"
                                        onClick={selectDeletedPages}
                                    >
                                        Só excluídas
                                    </button>
                                    {selectedPageIds.size > 0 && (
                                        <>
                                            <span className="text-gray-300">·</span>
                                            <button
                                                type="button"
                                                className="text-gray-500 hover:underline px-1.5"
                                                onClick={clearSelection}
                                            >
                                                Limpar
                                            </button>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>

                        {group.page_ids.length === 0 ? (
                            <p className="text-xs text-gray-500 italic px-3 py-6 text-center border rounded">
                                Nenhuma página vinculada ainda. Use a sincronização acima.
                            </p>
                        ) : (
                            <div className="border rounded max-h-[320px] overflow-y-auto divide-y">
                                {group.page_ids.map(pid => {
                                    const m = metaById.get(pid);
                                    const formInfo = formPagesById.get(pid);
                                    const label = m?.page_name
                                        || formInfo?.name
                                        || (m?.fb_page_id ? `FB: ${m.fb_page_id}` : `Página #${pid} (excluída)`);
                                    const isDeleted = !m && !formInfo;
                                    const isInactive = m && m.is_usable === false;
                                    const account = formInfo?.account;
                                    const isSelected = selectedPageIds.has(pid);
                                    return (
                                        <label
                                            key={pid}
                                            className={`flex items-center gap-3 px-3 py-2 cursor-pointer ${
                                                isSelected ? 'bg-violet-50' : 'hover:bg-gray-50'
                                            }`}
                                        >
                                            <Checkbox
                                                checked={isSelected}
                                                onCheckedChange={() => togglePageSelection(pid)}
                                            />
                                            <Globe className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-sm font-medium text-gray-900 truncate">
                                                        {label}
                                                    </span>
                                                    {isDeleted && (
                                                        <span className="inline-flex items-center gap-1 text-[10px] text-red-700 bg-red-100 px-1.5 py-0.5 rounded">
                                                            <AlertTriangle className="h-3 w-3" />excluída
                                                        </span>
                                                    )}
                                                    {isInactive && !isDeleted && (
                                                        <span className="text-[10px] text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                                                            inativa
                                                        </span>
                                                    )}
                                                </div>
                                                {account && (
                                                    <p className="text-[10px] text-gray-400">{account}</p>
                                                )}
                                            </div>
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
                                                disabled={removeMut.isPending || batchRemoveMut.isPending}
                                                title="Remover do grupo (cancela pendings)"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    removeMut.mutate(pid);
                                                }}
                                            >
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </Button>
                                        </label>
                                    );
                                })}
                            </div>
                        )}
                    </section>
                </div>

                <DialogFooter className="flex items-center justify-between gap-2 sm:justify-between">
                    {selectedPageIds.size > 0 ? (
                        <Button
                            variant="destructive"
                            disabled={batchRemoveMut.isPending}
                            onClick={() => batchRemoveMut.mutate(Array.from(selectedPageIds))}
                        >
                            {batchRemoveMut.isPending
                                ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Removendo...</>
                                : <><Trash2 className="h-3.5 w-3.5 mr-1.5" />Remover {selectedPageIds.size} selecionada{selectedPageIds.size === 1 ? '' : 's'}</>}
                        </Button>
                    ) : <span />}
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Fechar</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
